import React, { forwardRef, useState, useEffect } from 'react';
import { configService } from '../../api/configService';

const ProcurementInvoice = forwardRef(({ data }, ref) => {
  const [storeInfo, setStoreInfo] = useState({
    name: localStorage.getItem("tenantName") || "PRINTERA",
    address: "",
    phone: "",
    logoUrl: "",
    tagline: "",
  });

  useEffect(() => {
    let mounted = true;
    const loadStoreInfo = async () => {
      try {
        const response = await configService.getStore();
        const store = response?.data ?? response ?? {};
        if (!mounted) return;
        setStoreInfo({
          name: store?.name || localStorage.getItem("tenantName") || "PRINTERA",
          address: store?.settings?.address || store?.address || "",
          phone: store?.settings?.phone || store?.phone || "",
          logoUrl: store?.logo_url || store?.settings?.logo_url || localStorage.getItem("store_logo") || localStorage.getItem("tenantLogo") || "",
          tagline: store?.settings?.tagline || store?.tagline || store?.settings?.description || "Era Baru Bisnis Percetakan",
        });
      } catch (err) {
        if (!mounted) return;
        setStoreInfo((prev) => ({
          ...prev,
          name: localStorage.getItem("tenantName") || prev.name || "PRINTERA",
        }));
      }
    };

    loadStoreInfo();
    return () => {
      mounted = false;
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(Number(amount || 0));
  };

  const getStatusInfo = (status) => {
    switch (status?.toUpperCase()) {
      case 'DRAFT':
        return { label: 'Draft' };
      case 'PENDING':
      case 'WAITING_APPROVAL':
      case 'SUBMITTED':
        return { label: 'Menunggu Persetujuan' };
      case 'APPROVED':
        return { label: 'Disetujui' };
      case 'ORDERED':
      case 'ON_ORDER':
        return { label: 'Dipesan' };
      case 'SHIPPED':
      case 'IN_DELIVERY':
      case 'ON_DELIVERY':
        return { label: 'Dalam Pengiriman' };
      case 'PARTIAL':
      case 'PARTIAL_RECEIVED':
      case 'PARTIALLY_RECEIVED':
        return { label: 'Diterima Sebagian' };
      case 'RECEIVED':
      case 'COMPLETED':
        return { label: 'Diterima' };
      case 'REJECTED':
        return { label: 'Ditolak' };
      case 'CANCELLED':
      case 'CANCELED':
        return { label: 'Dibatalkan' };
      default:
        return { label: status || '-' };
    }
  };

  if (!data) {
    return (
      <div ref={ref} className="w-full max-w-[720px] bg-white p-8 text-center text-slate-400 font-sans">
        Memuat data Purchase Order...
      </div>
    );
  }

  const statusUpper = (data.status || "").toUpperCase();
  const isApproved = ['APPROVED', 'ORDERED', 'ON_ORDER', 'SHIPPED', 'IN_DELIVERY', 'ON_DELIVERY', 'PARTIAL', 'PARTIAL_RECEIVED', 'PARTIALLY_RECEIVED', 'RECEIVED', 'COMPLETED'].includes(statusUpper);
  const isRejected = statusUpper === 'REJECTED';
  const isCancelled = statusUpper === 'CANCELLED' || statusUpper === 'CANCELED';
  const statusInfo = getStatusInfo(data.status);

  const createdByName = data.created_by_user?.full_name || data.created_by_user?.name || data.created_by_name || data.creator_name || "-";
  const approvedByName = data.approved_by_user?.full_name || data.approved_by_user?.name || data.approved_by_name || data.approver_name || (isApproved && createdByName !== "-" ? createdByName : "-");

  const items = data.items || [];
  const subtotal = items.reduce((sum, item) => {
    const qty = Number(item.qty_ordered || item.quantity || 0);
    const price = Number(item.unit_price_estimated || item.unit_price || 0);
    return sum + (item.total_price_estimated || qty * price);
  }, 0);

  const totalAmount = data.total_estimated_amount ?? data.total_amount ?? subtotal;

  const cleanNotes = (() => {
    if (!data?.notes) return "";
    let n = data.notes
      .replace(/\[PIC:[^\]]*\]/gi, "")
      .replace(/\[Catatan Internal:[^\]]*\]/gi, "")
      .replace(/\[[^\]]*\]/g, "");
    const parts = n.split("|").map(p => p.trim()).filter(Boolean);
    return parts.join(" - ") || n.replace(/\|/g, "").trim();
  })();

  const rawPic = data.supplier?.contact_person || data.supplier?.pic || (data.notes?.match(/\[PIC:\s*([^\]]+)\]/)?.[1]?.trim()) || "";
  const cleanPicName = rawPic ? rawPic.split(/[\-\/]/)[0].trim() : "-";

  return (
    <div
      ref={ref}
      className="w-full max-w-[820px] bg-white p-6 sm:p-8 print:p-0 text-slate-900 font-sans shadow-md border border-slate-200 rounded-xl print:shadow-none print:border-none print:m-0 print:max-w-none print:w-full print:rounded-none box-border text-xs print:text-[10px] leading-normal print:leading-tight">
      
      {/* Header Section (Store Name on Left, PURCHASE ORDER on Right) */}
      <div className="flex justify-between items-start gap-4 print:gap-2">
        {/* Left: Logo + Company Info */}
        <div className="flex items-start gap-3 print:gap-2 min-w-0 flex-1 pr-2">
          {storeInfo.logoUrl && (
            <img
              src={storeInfo.logoUrl}
              alt={storeInfo.name}
              className="w-12 h-12 sm:w-14 sm:h-14 print:w-10 print:h-10 object-contain shrink-0"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )}
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl print:text-base font-black tracking-tight text-slate-950 uppercase leading-tight mb-0.5">
              {storeInfo.name}
            </h1>
            {storeInfo.tagline && (
              <p className="text-[11px] sm:text-xs print:text-[9px] text-slate-600 font-medium leading-tight">
                {storeInfo.tagline}
              </p>
            )}
            {storeInfo.address && (
              <p className="text-[11px] print:text-[9px] text-slate-500 leading-tight mt-0.5 break-words">
                {storeInfo.address}
              </p>
            )}
            {storeInfo.phone && (
              <p className="text-[11px] print:text-[9px] text-slate-500 leading-tight mt-0.5">
                Telp: {storeInfo.phone}
              </p>
            )}
          </div>
        </div>

        {/* Right: Document Title, PO Number & Status Badge */}
        <div className="shrink-0 text-right flex flex-col items-end">
          <h2 className="text-xl sm:text-2xl print:text-base font-black tracking-tight text-slate-950 uppercase leading-none whitespace-nowrap mb-1">
            PURCHASE ORDER
          </h2>
          <p className="text-sm print:text-xs font-bold text-slate-800 font-mono whitespace-nowrap mb-1">
            {data.po_number || "-"}
          </p>
          <span className="inline-block border border-slate-300 text-slate-700 text-[11px] print:text-[9px] font-semibold px-3 py-0.5 print:py-0 rounded-full bg-white whitespace-nowrap">
            {statusInfo.label}
          </span>
        </div>
      </div>

      {/* Solid Divider Bar */}
      <div className="border-b-2 border-slate-900 my-3 print:my-1.5"></div>

      {/* Two Column Details: SUPPLIER on Left, INFORMASI PO on Right */}
      <div className="grid grid-cols-2 gap-4 print:gap-3 text-[11px] sm:text-xs print:text-[9.5px] text-slate-800 my-3 print:my-1.5">
        {/* Left: Supplier Information (4 Baris) */}
        <div className="space-y-1">
          <h3 className="font-bold text-slate-900 uppercase tracking-wider text-[11px] print:text-[9px] mb-1 print:mb-0.5 pb-0.5 border-b border-slate-200">
            INFORMASI SUPPLIER
          </h3>
          <div className="grid grid-cols-3 gap-y-0.5">
            <span className="text-slate-500 font-medium">Nama:</span>
            <span className="col-span-2 font-bold text-slate-900 leading-snug">
              {data.supplier?.name || data.supplier_name || "-"}
            </span>

            <span className="text-slate-500 font-medium">No. Telepon:</span>
            <span className="col-span-2 font-semibold text-slate-800">
              {data.supplier?.phone_number || data.supplier?.phone || "-"}
            </span>

            <span className="text-slate-500 font-medium">Alamat:</span>
            <span className="col-span-2 font-medium text-slate-700 leading-tight break-words">
              {data.supplier?.address || "-"}
            </span>

            <span className="text-slate-500 font-medium">Nama PIC:</span>
            <span className="col-span-2 font-medium text-slate-700">
              {cleanPicName}
            </span>
          </div>
        </div>

        {/* Right: PO Information (4 Baris) */}
        <div className="space-y-1 pl-3 print:pl-2 border-l border-slate-200">
          <h3 className="font-bold text-slate-900 uppercase tracking-wider text-[11px] print:text-[9px] mb-1 print:mb-0.5 pb-0.5 border-b border-slate-200">
            INFORMASI PURCHASE ORDER
          </h3>
          <div className="grid grid-cols-3 gap-y-0.5">
            <span className="text-slate-500 font-medium">Tanggal Order:</span>
            <span className="col-span-2 font-semibold text-slate-800">
              {formatDate(data.request_date || data.order_date || data.created_at)}
            </span>

            <span className="text-slate-500 font-medium">Estimasi Tiba:</span>
            <span className="col-span-2 font-semibold text-slate-800">
              {formatDate(data.due_date || data.expected_delivery_date || data.estimated_arrival_date || data.request_date)}
            </span>

            <span className="text-slate-500 font-medium">Dibuat Oleh:</span>
            <span className="col-span-2 font-semibold text-slate-800">
              {createdByName}
            </span>

            <span className="text-slate-500 font-medium">Disetujui Oleh:</span>
            <span className={`col-span-2 font-semibold ${isApproved ? 'text-slate-800' : 'text-slate-500 italic'}`}>
              {isApproved ? approvedByName : (isRejected ? "Ditolak" : isCancelled ? "Dibatalkan" : "Belum Disetujui")}
            </span>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="my-3 print:my-1.5 overflow-x-auto">
        <table className="w-full border-collapse border border-slate-300 text-[11px] sm:text-xs print:text-[9.5px]">
          <thead>
            <tr className="bg-slate-100 text-slate-900 font-bold border-b border-slate-300">
              <th className="border border-slate-300 px-2.5 py-1.5 print:px-2 print:py-0.5 text-center w-10">NO</th>
              <th className="border border-slate-300 px-2.5 py-1.5 print:px-2 print:py-0.5 text-left">NAMA ITEM</th>
              <th className="border border-slate-300 px-2.5 py-1.5 print:px-2 print:py-0.5 text-center w-20">QTY</th>
              <th className="border border-slate-300 px-2.5 py-1.5 print:px-2 print:py-0.5 text-right w-28">HARGA SATUAN</th>
              <th className="border border-slate-300 px-2.5 py-1.5 print:px-2 print:py-0.5 text-right w-28">SUBTOTAL</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-300">
            {items.map((item, index) => {
              const qty = Number(item.qty_ordered || item.quantity || 0);
              const price = Number(item.unit_price_estimated || item.unit_price || 0);
              const rowTotal = Number(item.total_price_estimated || qty * price);
              const satuan = item.product_unit_name || item.satuan_name || item.satuan || "Pcs";

              return (
                <tr key={index} className="hover:bg-slate-50/50">
                  <td className="border border-slate-300 px-2.5 py-1.5 print:px-2 print:py-0.5 text-center font-medium text-slate-700">
                    {index + 1}
                  </td>
                  <td className="border border-slate-300 px-2.5 py-1.5 print:px-2 print:py-0.5 text-left font-bold text-slate-900">
                    <div>
                      {item.product_name || item.name || "-"}
                      {(item.brand_name || item.brand || item.variant_name) ? (
                        <span className="font-normal text-slate-600"> ({item.brand_name || item.brand || item.variant_name})</span>
                      ) : ""}
                    </div>
                    {(item.unit_name || item.asset_unit_name) && (
                      <div className="text-[10px] print:text-[8.5px] font-semibold text-teal-800 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded inline-block mt-0.5">
                        Target Unit: {item.unit_name || item.asset_unit_name}
                      </div>
                    )}
                  </td>
                  <td className="border border-slate-300 px-2.5 py-1.5 print:px-2 print:py-0.5 text-center font-semibold text-slate-800 whitespace-nowrap">
                    {qty} {satuan}
                  </td>
                  <td className="border border-slate-300 px-2.5 py-1.5 print:px-2 print:py-0.5 text-right font-medium text-slate-800 whitespace-nowrap">
                    {formatCurrency(price)}
                  </td>
                  <td className="border border-slate-300 px-2.5 py-1.5 print:px-2 print:py-0.5 text-right font-bold text-slate-950 whitespace-nowrap">
                    {formatCurrency(rowTotal)}
                  </td>
                </tr>
              );
            })}

            {items.length === 0 && (
              <tr>
                <td colSpan="5" className="border border-slate-300 px-3 py-6 text-center text-slate-400">
                  Tidak ada item dalam Purchase Order ini
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50/60 font-bold">
              <td colSpan="4" className="border border-slate-300 px-3 py-1.5 print:px-2 print:py-0.5 text-right text-slate-800">
                Subtotal
              </td>
              <td className="border border-slate-300 px-2.5 py-1.5 print:px-2 print:py-0.5 text-right font-bold text-slate-900 whitespace-nowrap">
                {formatCurrency(subtotal)}
              </td>
            </tr>
            <tr className="bg-slate-100/90 font-extrabold">
              <td colSpan="4" className="border border-slate-300 px-3 py-2 print:px-2 print:py-1 text-right text-slate-950 text-xs print:text-[10px] uppercase tracking-wide">
                Total Nilai PO
              </td>
              <td className="border border-slate-300 px-2.5 py-2 print:px-2 print:py-1 text-right font-extrabold text-slate-950 text-xs sm:text-sm print:text-xs whitespace-nowrap">
                {formatCurrency(totalAmount)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Catatan Box */}
      <div className="border border-slate-300 p-2.5 print:p-1.5 rounded-none text-[11px] print:text-[9.5px] my-3 print:my-1 bg-white">
        <span className="font-bold text-slate-900 block mb-0.5">Catatan:</span>
        <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
          {cleanNotes || "-"}
        </p>
      </div>

      {/* Signatures Section (3 Columns: Dibuat Oleh, Disetujui Oleh, Supplier) */}
      <div className="grid grid-cols-3 gap-3 text-center text-[11px] print:text-[9.5px] mt-6 pt-1 print:mt-2 print:pt-0 break-inside-avoid">
        {/* Kolom 1: Dibuat Oleh */}
        <div className="flex flex-col items-center">
          <span className="font-medium text-slate-800 mb-10 sm:mb-12 print:mb-5">Dibuat Oleh</span>
          <div className="w-28 sm:w-36 print:w-28 border-b border-slate-900 mb-1"></div>
          <span className="font-bold text-slate-900">{createdByName}</span>
        </div>

        {/* Kolom 2: Disetujui Oleh */}
        <div className="flex flex-col items-center">
          <span className="font-medium text-slate-800 mb-10 sm:mb-12 print:mb-5">Disetujui Oleh</span>
          <div className="w-28 sm:w-36 print:w-28 border-b border-slate-900 mb-1"></div>
          <span className={`font-bold ${isApproved ? 'text-slate-900' : 'text-slate-400 italic'}`}>
            {isApproved ? approvedByName : (isRejected ? "(Ditolak)" : isCancelled ? "(Dibatalkan)" : "(Belum Disetujui)")}
          </span>
        </div>

        {/* Kolom 3: Supplier */}
        <div className="flex flex-col items-center">
          <span className="font-medium text-slate-800 mb-10 sm:mb-12 print:mb-5">Supplier</span>
          <div className="w-28 sm:w-36 print:w-28 border-b border-slate-900 mb-1"></div>
          <span className="font-bold text-slate-900">
            {data.supplier?.name || data.supplier_name || "Supplier"}
          </span>
        </div>
      </div>
    </div>
  );
});

ProcurementInvoice.displayName = 'ProcurementInvoice';

export default ProcurementInvoice;
